import { Fragment } from 'react';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import { useTranslation } from 'react-i18next';
import makeStyles from '@material-ui/styles/makeStyles';
import { grayColor } from 'assets/jss/material-dashboard-pro-react';
import CustomInput from 'components/CustomInput/CustomInput';
import RegularCheckbox from 'components/Checkbox/RegularCheckbox';
import InfoIconTooltip from 'components/InfoIconTooltip/InfoIconTooltip';
import { sortByPosition } from 'helpers/helpers';

import { getBrandDishSizes, getDishSizeKey } from './shopSaleHelpers';

const useStyles = makeStyles({
  cell: {
    padding: '12px 24px',
    borderBottom: `1px solid ${grayColor[21]}`,
    color: grayColor[16],
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '20px',
  },
  headerRow: {
    backgroundColor: grayColor[4],
  },
  headerCell: {
    color: grayColor[2],
    fontSize: '12px',
    fontWeight: 500,
    lineHeight: 1.2,
  },
  brandRow: {
    backgroundColor: grayColor[8],
  },
  brandName: {
    color: grayColor[2],
    fontSize: '16px',
    fontWeight: 600,
    lineHeight: 1.25,
    textTransform: 'uppercase',
  },
  mealTypeName: {
    color: grayColor[2],
    fontSize: '14px',
    fontWeight: 500,
    lineHeight: 1.25,
  },
  priceCell: {
    width: '190px',
  },
  availabilityCell: {
    width: '238px',
    textAlign: 'center',
  },
  allSizesControl: {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    margin: 0,
    cursor: 'pointer',
  },
  allSizesText: {
    position: 'absolute',
    left: '100%',
    color: grayColor[1],
    fontSize: '14px',
    fontWeight: 400,
    whiteSpace: 'nowrap',
  },
});

const ShopSaleTable = ({
  dishSizes,
  groupedTypes,
  isAvailableInFixedOffer,
  handleSizeFixedOfferChange,
  handleBrandFixedOfferChange,
  handlePriceChange,
}) => {
  const { t } = useTranslation();
  const styles = useStyles();

  const columnCount = isAvailableInFixedOffer ? 3 : 2;

  return (
    <div style={{ overflow: 'auto' }}>
      <Table>
        <TableHead>
          <TableRow className={styles.headerRow}>
            <TableCell className={`${styles.cell} ${styles.headerCell}`}>
              {t('$*mealForm.shopSale.size')}
            </TableCell>
            <TableCell
              className={`${styles.cell} ${styles.headerCell} ${styles.priceCell}`}
            >
              <InfoIconTooltip title={t('$*mealForm.shopSale.price.tooltip')}>
                {t('$*mealForm.shopSale.price')}
              </InfoIconTooltip>
            </TableCell>
            {isAvailableInFixedOffer && (
              <TableCell
                className={`${styles.cell} ${styles.headerCell} ${styles.availabilityCell}`}
              >
                {t('$*mealForm.shopSale.sizesInFixedOffer')}
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {groupedTypes.map(brand => {
            const brandDishSizes = getBrandDishSizes(brand.types, dishSizes);
            const isWholeBrandChecked =
              brandDishSizes.length > 0 &&
              brandDishSizes.every(
                ({ availableInFixedOffer }) => availableInFixedOffer
              );

            return (
              <Fragment key={brand['@id']}>
                <TableRow className={styles.brandRow}>
                  <TableCell
                    className={`${styles.cell} ${styles.brandName}`}
                    colSpan={isAvailableInFixedOffer ? 2 : columnCount}
                  >
                    {brand.name}
                  </TableCell>
                  {isAvailableInFixedOffer && (
                    <TableCell
                      className={`${styles.cell} ${styles.availabilityCell}`}
                    >
                      <label className={styles.allSizesControl}>
                        <RegularCheckbox
                          checked={isWholeBrandChecked}
                          onChange={() =>
                            handleBrandFixedOfferChange(
                              brandDishSizes,
                              !isWholeBrandChecked
                            )
                          }
                          disabled={brandDishSizes.length === 0}
                        />
                        <span className={styles.allSizesText}>
                          {t('$*mealForm.shopSale.allSizes')}
                        </span>
                      </label>
                    </TableCell>
                  )}
                </TableRow>
                {sortByPosition(brand.types).map(type => (
                  <Fragment key={type['@id']}>
                    <TableRow>
                      <TableCell
                        className={`${styles.cell} ${styles.mealTypeName}`}
                        colSpan={columnCount}
                      >
                        {type.name}
                      </TableCell>
                    </TableRow>
                    {sortByPosition(type.sizes).map(typeSize => {
                      const dishSize = dishSizes.find(
                        size =>
                          size.mealType['@id'] === type['@id'] &&
                          size.size === typeSize.size['@id']
                      );

                      if (dishSize === undefined) {
                        return null;
                      }

                      const dishSizeKey = getDishSizeKey(dishSize);

                      return (
                        <TableRow key={dishSizeKey}>
                          <TableCell className={styles.cell}>
                            {typeSize.size.name}
                          </TableCell>
                          <TableCell
                            className={`${styles.cell} ${styles.priceCell}`}
                          >
                            <CustomInput
                              formControlProps={{ fullWidth: true }}
                              maxLength={160}
                              inputProps={{
                                placeholder: '',
                                value: dishSize.costInShop ?? '',
                                onChange: ev => handlePriceChange(ev, dishSize),
                              }}
                            />
                          </TableCell>
                          {isAvailableInFixedOffer && (
                            <TableCell
                              className={`${styles.cell} ${styles.availabilityCell}`}
                            >
                              <RegularCheckbox
                                checked={
                                  dishSize.availableInFixedOffer ?? false
                                }
                                onChange={() =>
                                  handleSizeFixedOfferChange(
                                    !dishSize.availableInFixedOffer,
                                    dishSize
                                  )
                                }
                              />
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                  </Fragment>
                ))}
              </Fragment>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};

export default ShopSaleTable;
